import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import PublicShell from "./components/PublicShell";
import PhoneVerify from "./components/PhoneVerify";
import VehicleDetailsForm from "./components/VehicleDetailsForm";
import { Car, Check, Plus } from "./components/icons";
import { apiErrorMessage, buildVehicleFormData, portalApi, readSession, writeSession } from "./publicVehicleApi";
import styles from "./publicvehicle.module.css";

const SCOPE = "tenant";
const phoneRegExp = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

const formatDate = (value) => (value ? dayjs(value).format("MMM D, YYYY") : "");
const formatDateTime = (value) => (value ? dayjs(value).format("MMM D, h:mm A") : "");
const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("") || "G";
const vehicleLine = (v) => [v?.make, v?.model].filter(Boolean).join(" ") + (v?.color ? ` · ${v.color}` : "");

// Links opened from the tenant app end with #code=<one-time code> (valid 10 minutes) that replaces the SMS code
const readAppCode = () => new URLSearchParams((window.location.hash || "").replace(/^#/, "")).get("code") || "";

// Guest statuses from the API -> chip text, chip style, detail line
const guestStatus = (invite) => {
  const first = (invite.guest_name || "").split(" ")[0] || "your guest";
  switch (invite.status) {
    case "INVITED": return { chip: "Link sent", tone: "neutral", detail: `Waiting for ${first} to add their vehicle` };
    case "EXPIRED": return { chip: "Link expired", tone: "neutral", detail: "The link expired before your guest used it" };
    case "PENDING": return { chip: "Pending approval", tone: "pending", detail: `Submitted ${formatDate(invite.submitted_at)} · waiting for property manager` };
    case "AWAITING_PAYMENT": return { chip: "Approved", tone: "approved", detail: `Approved ${formatDate(invite.reviewed_at)} · parking fee link sent to ${first}` };
    case "PAYMENT_EXPIRED": return { chip: "Not paid", tone: "neutral", detail: "The parking fee was not paid in time" };
    case "ACTIVE": return { chip: "Pass active", tone: "approved", detail: `Parking pass valid until ${formatDateTime(invite.pass_expires_at)}` };
    case "PASS_ENDED": return { chip: "Pass ended", tone: "neutral", detail: `Pass ended ${formatDateTime(invite.pass_expires_at)}` };
    case "REJECTED": return { chip: "Not approved", tone: "rejected", detail: invite.review_note || "Not approved by the property manager" };
    case "CANCELLED": return { chip: "Cancelled", tone: "neutral", detail: "Invitation cancelled" };
    default: return { chip: invite.status, tone: "neutral", detail: "" };
  }
};

const GuestInviteForm = ({ submitting, error, onSubmit, onCancel }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (name.trim() === "") next.name = "Enter your guest's name";
    if (!phoneRegExp.test(phone.trim())) next.phone = "Enter a valid 10-digit phone number";
    setErrors(next);
    if (Object.keys(next).length) return;
    const parts = name.trim().split(/\s+/);
    onSubmit({ guest_fname: parts[0], guest_lname: parts.slice(1).join(" "), guest_phone: phone.trim() });
  };

  return (<form className={styles.form} onSubmit={submit} noValidate>
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Add guest vehicle</h2>
      <div className={styles.field}>
        <label htmlFor="g-name" className={styles.label}>Guest name*</label>
        <input id="g-name" className={`${styles.input} ${errors.name ? styles.inputError : ""}`} value={name} maxLength={100} disabled={submitting} onChange={(e) => setName(e.target.value)} placeholder="Sam Carter" />
        {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
      </div>
      <div className={styles.field}>
        <label htmlFor="g-phone" className={styles.label}>Guest phone number*</label>
        <div className={`${styles.phoneInput} ${errors.phone ? styles.inputError : ""}`}>
          <span>+1</span>
          <input id="g-phone" type="tel" inputMode="tel" value={phone} disabled={submitting} onChange={(e) => setPhone(e.target.value)} placeholder="(917) 555-0142" />
        </div>
        {errors.phone && <span className={styles.fieldError}>{errors.phone}</span>}
      </div>
      <div className={styles.infoBox}>
        <p className={styles.infoTitle}>We'll text your guest a secure link</p>
        <ol className={styles.numberedList}>
          <li>Your guest opens the link and verifies their phone number.</li>
          <li>They add their own vehicle number, make, model, color and photos.</li>
          <li>The vehicle shows as Pending approval until your property manager approves it. Then your guest pays the parking fee to activate the pass.</li>
        </ol>
      </div>
      {error !== "" && <Alert severity="error">{error}</Alert>}
    </section>
    <div className={styles.actions}>
      <button type="button" className={styles.secondaryButton} onClick={onCancel} disabled={submitting}>Cancel</button>
      <button type="submit" className={styles.primaryButton} disabled={submitting}>{submitting ? "SENDING..." : "SEND LINK"}</button>
    </div>
  </form>);
};

const TenantVehiclePortal = () => {
  const { token } = useParams();
  const location = useLocation();
  const vehicleParam = new URLSearchParams(location.search).get("vehicle") || "";
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState({ status: 0, message: "" });
  const [session, setSession] = useState(() => readSession(SCOPE, token));
  const [appCode] = useState(readAppCode);
  const [appLoginPending, setAppLoginPending] = useState(() => appCode !== "" && !readSession(SCOPE, token));
  const [appLoginError, setAppLoginError] = useState("");
  const appLoginStartedRef = useRef(false);
  const openedVehicleRef = useRef(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [view, setView] = useState("list"); // list | form | guest | submitted
  const [editing, setEditing] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [rowBusyId, setRowBusyId] = useState(0);

  const dropSession = useCallback(() => {
    writeSession(SCOPE, token, "", 0);
    setSession("");
    setData(null);
  }, [token]);

  const handleError = useCallback((err, fallback) => {
    if (err?.response?.data?.reverify) {
      dropSession();
      return "Please verify your phone number again.";
    }
    return apiErrorMessage(err, fallback);
  }, [dropSession]);

  useEffect(() => {
    async function loadInfo() {
      try {
        const response = await portalApi.info(token);
        setInfo(response?.data?.data || null);
      } catch (err) {
        console.error("[TenantVehiclePortal] Failed to load link", err);
        setInfoError({ status: err?.response?.status || 0, message: apiErrorMessage(err, "Unable to load this link. Please try again.") });
      } finally {
        setLoading(false);
      }
    }
    loadInfo();
  }, [token]);

  const load = useCallback(async (openFormIfEmpty) => {
    if (!session) return;
    setLoadError("");
    try {
      const response = await portalApi.get(token, session);
      const next = response?.data?.data || null;
      setData(next);
      if (openFormIfEmpty && next && (next.vehicles || []).length === 0 && (next.requests || []).length === 0 && (next.guest?.invites || []).length === 0) {
        setView("form");
      }
    } catch (err) {
      console.error("[TenantVehiclePortal] Failed to load vehicles", err);
      setLoadError(handleError(err, "Unable to load your vehicles. Please try again."));
    }
  }, [token, session, handleError]);

  useEffect(() => {
    load(true);
  }, [load]);

  // Drop the one-time code from the address bar so a refresh or a shared URL doesn't carry it
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
  }, []);

  // The code is single use, so it must be sent only once (StrictMode runs effects twice in development)
  useEffect(() => {
    if (!appLoginPending || appLoginStartedRef.current) return;
    appLoginStartedRef.current = true;
    async function appLogin() {
      try {
        const response = await portalApi.appLogin(token, appCode);
        const value = response?.data?.session_token || "";
        writeSession(SCOPE, token, value, response?.data?.expires_in || 3600);
        setSession(value);
      } catch (err) {
        console.error("[TenantVehiclePortal] App link sign-in failed", err);
        setAppLoginError(apiErrorMessage(err, "Please verify your phone number to continue."));
      } finally {
        setAppLoginPending(false);
      }
    }
    appLogin();
  }, [appLoginPending, appCode, token]);

  // ?vehicle=<id> (from the tenant app's vehicle screen) opens that vehicle's edit form once
  useEffect(() => {
    if (!data || !vehicleParam || openedVehicleRef.current) return;
    openedVehicleRef.current = true;
    const vehicle = (data.vehicles || []).find((v) => String(v.id) === vehicleParam);
    if (vehicle) {
      setEditing(vehicle);
      setView("form");
    } else {
      setNotice("That vehicle is no longer on your account. Here are your current vehicles.");
    }
  }, [data, vehicleParam]);

  const sendCode = async (phone) => {
    try {
      const response = await portalApi.sendOtp(token, phone);
      setInfo((prev) => ({ ...prev, phone_hint: response?.data?.phone_hint || prev?.phone_hint }));
      return response?.data?.otp_token || "";
    } catch (err) {
      console.error("[TenantVehiclePortal] Failed to send code", err);
      throw apiErrorMessage(err, "Unable to send the code. Please try again.");
    }
  };

  const verifyCode = async (code, otpToken) => {
    try {
      const response = await portalApi.verifyOtp(token, { code, otp_token: otpToken });
      const value = response?.data?.session_token || "";
      writeSession(SCOPE, token, value, response?.data?.expires_in || 3600);
      setSession(value);
    } catch (err) {
      console.error("[TenantVehiclePortal] Failed to verify code", err);
      throw apiErrorMessage(err, "Unable to verify the code. Please try again.");
    }
  };

  const submitVehicle = async (values, photos) => {
    setBusy(true);
    setFormError("");
    try {
      const extra = { session };
      if (editing) extra.vehicle_id = editing.id;
      const response = await portalApi.submitVehicle(token, buildVehicleFormData(values, photos, extra));
      setSubmitted({ ...values, photo_count: response?.data?.data?.photo_count ?? photos.length, isUpdate: !!editing });
      setEditing(null);
      setView("submitted");
      await load(false);
    } catch (err) {
      console.error("[TenantVehiclePortal] Failed to submit vehicle", err);
      setFormError(handleError(err, "Unable to submit your vehicle. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const inviteGuest = async (values) => {
    setBusy(true);
    setFormError("");
    try {
      const response = await portalApi.inviteGuest(token, { ...values, session });
      setNotice(response?.data?.message || "We texted your guest a secure link.");
      setView("list");
      await load(false);
    } catch (err) {
      console.error("[TenantVehiclePortal] Failed to invite guest", err);
      setFormError(handleError(err, "Unable to invite your guest. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const guestAction = async (action, id) => {
    setRowBusyId(id);
    setNotice("");
    setLoadError("");
    try {
      const response = action === "resend" ? await portalApi.resendInvite(token, id, session) : await portalApi.cancelInvite(token, id, session);
      setNotice(response?.data?.message || "Done.");
      await load(false);
    } catch (err) {
      console.error(`[TenantVehiclePortal] Failed to ${action} guest invite`, err);
      setLoadError(handleError(err, `Unable to ${action} the invitation. Please try again.`));
    } finally {
      setRowBusyId(0);
    }
  };

  const shellProps = {
    propertyName: info?.property_name,
    propertyAddress: info?.property_address,
    propertyPhone: info?.property_phone,
    context: info ? `Apt Unit ${info.apt_no || ""} · ${info.tenant_name || ""}` : "",
  };

  if (loading || appLoginPending) {
    return (<PublicShell title="Your vehicles" {...shellProps}><div className={styles.center}><CircularProgress size={30} /></div></PublicShell>);
  }

  if (!info) {
    const expired = infoError.status === 404 || infoError.status === 410;
    return (<PublicShell title="Your vehicles">
      <Alert severity={expired ? "warning" : "error"}>{infoError.message}</Alert>
      <div className={styles.actions}>
        {expired ? <Link to="/vehicle-update" className={styles.primaryButton}>REQUEST A NEW LINK</Link> : <button className={styles.primaryButton} onClick={() => window.location.reload()}>TRY AGAIN</button>}
      </div>
    </PublicShell>);
  }

  if (!session) {
    return (<PublicShell title="Add your vehicle" {...shellProps} steps={[{ label: "Verify phone", state: "active" }, { label: "Vehicle details", state: "todo" }]}>
      {appLoginError !== "" && <Alert severity="info" className={styles.block}>{appLoginError}</Alert>}
      <PhoneVerify askPhone phoneHint={info.phone_hint} sendCode={sendCode} verifyCode={verifyCode} />
    </PublicShell>);
  }

  if (!data) {
    return (<PublicShell title="My Vehicles" {...shellProps}>
      {loadError !== "" ? (<>
        <Alert severity="error">{loadError}</Alert>
        <div className={styles.actions}><button className={styles.primaryButton} onClick={() => load(true)}>TRY AGAIN</button></div>
      </>) : (<div className={styles.center}><CircularProgress size={30} /></div>)}
    </PublicShell>);
  }

  if (view === "form") {
    return (<PublicShell title={editing ? "Edit vehicle" : "Add your vehicle"} {...shellProps} steps={[{ label: "Phone verified", state: "done" }, { label: "Vehicle details", state: "active" }]}>
      {formError !== "" && <Alert severity="error" className={styles.block}>{formError}</Alert>}
      <VehicleDetailsForm
        defaultValues={editing}
        requirePhotos={!editing}
        minPhotos={data.min_photos || 2}
        maxPhotos={data.max_photos || 6}
        submitting={busy}
        submitLabel={editing ? "SAVE CHANGES" : "SUBMIT VEHICLE"}
        compact={Boolean(editing)}
        onSubmit={submitVehicle}
        onCancel={() => { setEditing(null); setFormError(""); setView("list"); }}
      />
      <p className={styles.lockNote}>Secure link for {info.tenant_name} only</p>
    </PublicShell>);
  }

  if (view === "guest") {
    return (<PublicShell title="Add guest vehicle" {...shellProps}>
      <GuestInviteForm submitting={busy} error={formError} onSubmit={inviteGuest} onCancel={() => { setFormError(""); setView("list"); }} />
    </PublicShell>);
  }

  if (view === "submitted" && submitted) {
    return (<PublicShell title="Vehicle submitted" {...shellProps}>
      <section className={`${styles.card} ${styles.centerText}`}>
        <div className={styles.successCircle}><Check /></div>
        <h2 className={styles.cardTitle}>Thanks, {data?.tenant?.first_name || info.first_name}</h2>
        <p className={styles.cardText}>Your {submitted.isUpdate ? "changes were" : "vehicle was"} sent to the property manager for approval. You'll get a text once it's reviewed.</p>
      </section>
      <section className={styles.card}>
        <p className={styles.sectionLabel}>SUMMARY</p>
        <dl className={styles.summaryList}>
          <div><dt>Status</dt><dd><span className={`${styles.chip} ${styles.chip_pending}`}>Pending approval</span></dd></div>
          <div><dt>Vehicle number</dt><dd>{submitted.license}</dd></div>
          <div><dt>Make &amp; model</dt><dd>{submitted.make} {submitted.model}</dd></div>
          <div><dt>Color</dt><dd>{submitted.color}</dd></div>
          <div><dt>Photos</dt><dd>{submitted.photo_count > 0 ? `${submitted.photo_count} uploaded` : "No new photos"}</dd></div>
        </dl>
      </section>
      <div className={styles.actions}><button className={styles.primaryButton} onClick={() => { setSubmitted(null); setView("list"); }}>BACK TO MY VEHICLES</button></div>
    </PublicShell>);
  }

  const vehicles = data.vehicles || [];
  const newRequests = (data.requests || []).filter((r) => r.type === "TENANT_NEW" && (r.status === "PENDING" || r.status === "REJECTED"));
  const invites = (data.guest?.invites || []).filter((i) => i.status !== "CANCELLED");

  return (<PublicShell title="My Vehicles" {...shellProps}>
    {notice !== "" && <Alert severity="success" onClose={() => setNotice("")} className={styles.block}>{notice}</Alert>}
    {loadError !== "" && <Alert severity="error" onClose={() => setLoadError("")} className={styles.block}>{loadError}</Alert>}

    <div className={styles.listHead}><span className={styles.sectionLabel}>MY VEHICLE</span></div>
    {vehicles.length === 0 && newRequests.length === 0 && (<section className={`${styles.card} ${styles.centerText}`}>
      <div className={styles.emptyIcon}><Car size={44} /></div>
      <p className={styles.emptyTitle}>No vehicle added yet</p>
    </section>)}
    <div className={styles.listCard}>
      {vehicles.map((vehicle) => (<button key={vehicle.id} type="button" className={styles.vehicleRow} onClick={() => { setEditing(vehicle); setNotice(""); setView("form"); }}>
        {vehicle.image_path ? <img src={vehicle.image_path} alt="" className={styles.vehicleThumb} /> : <span className={styles.vehicleThumb}><Car size={30} /></span>}
        <span className={styles.vehicleInfo}>
          <span className={styles.vehicleName}>{[vehicle.make, vehicle.model].filter(Boolean).join(" ")}</span>
          <span className={styles.vehicleMeta}><span className={styles.plate}>{vehicle.license}</span>{vehicle.color}</span>
          {vehicle.has_pending_update && <span className={`${styles.chip} ${styles.chip_pending}`}>Changes pending approval</span>}
        </span>
        <span className={styles.rowAction}>Edit</span>
      </button>))}
      {newRequests.map((item) => (<div key={"r" + item.id} className={styles.vehicleRow}>
        <span className={styles.vehicleThumb}><Car size={30} /></span>
        <span className={styles.vehicleInfo}>
          <span className={styles.vehicleName}>{[item.make, item.model].filter(Boolean).join(" ")}</span>
          <span className={styles.vehicleMeta}><span className={styles.plate}>{item.license}</span>{item.color}</span>
          <span className={`${styles.chip} ${item.status === "REJECTED" ? styles.chip_rejected : styles.chip_pending}`}>{item.status === "REJECTED" ? "Not approved" : "Pending approval"}</span>
          {item.status === "REJECTED" && item.review_note && <span className={styles.muted}>{item.review_note}</span>}
        </span>
      </div>))}
    </div>
    <button type="button" className={`${styles.secondaryButton} ${styles.fullWidth}`} onClick={() => { setEditing(null); setNotice(""); setView("form"); }}><Plus /> Add a vehicle</button>

    <div className={styles.listHead}><span className={styles.sectionLabel}>GUEST VEHICLES</span><span className={styles.muted}>{invites.length}</span></div>
    {invites.length === 0 ? (<section className={`${styles.card} ${styles.centerText}`}>
      <p className={styles.emptyTitle}>No guest vehicles</p>
      <p className={styles.cardText}>Expecting a visitor? Add their vehicle so the property manager can approve it.</p>
    </section>) : (<div className={styles.listCard}>
      {invites.map((invite) => {
        const s = guestStatus(invite);
        return (<div key={invite.id} className={styles.guestRow}>
          <span className={styles.avatar}>{initials(invite.guest_name)}</span>
          <div className={styles.vehicleInfo}>
            <div className={styles.guestTop}><span className={styles.vehicleName}>{invite.guest_name}</span><span className={`${styles.chip} ${styles["chip_" + s.tone]}`}>{s.chip}</span></div>
            {invite.license && <span className={styles.vehicleMeta}><span className={styles.plate}>{invite.license}</span>{vehicleLine(invite)}</span>}
            <span className={styles.muted}>{s.detail}</span>
            <div className={styles.rowButtons}>
              {(invite.status === "INVITED" || invite.status === "EXPIRED") && <button type="button" className={styles.linkButton} disabled={rowBusyId === invite.id} onClick={() => guestAction("resend", invite.id)}>{rowBusyId === invite.id ? "Sending..." : "Resend link"}</button>}
              {["INVITED", "PENDING", "AWAITING_PAYMENT"].includes(invite.status) && <button type="button" className={styles.linkButton} disabled={rowBusyId === invite.id} onClick={() => guestAction("cancel", invite.id)}>Cancel</button>}
            </div>
          </div>
        </div>);
      })}
    </div>)}

    {data.guest?.can_invite
      ? <button type="button" className={`${styles.primaryButton} ${styles.fullWidth}`} onClick={() => { setFormError(""); setNotice(""); setView("guest"); }}><Plus /> ADD GUEST VEHICLE</button>
      : <Alert severity="info">Your household can have {data.guest?.limit || 1} active guest vehicle at a time. You can add another once the current pass ends, or cancel an invitation that hasn't been paid.</Alert>}
  </PublicShell>);
};

export default TenantVehiclePortal;
